import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './styles';
import Queries from '../../../../utils/API/Queries';
import UILoadingPage from '../../../../components/UI/UILoadingPage/UILoadingPage';
import { USER_ROLE_ADMIN, USER_ROLE_AFFILIATE, USER_ROLE_REGIONAL_MANAGER } from '../../../../utils/Config/Constants';
import PLPermissionBoundary from '../../../../components/Boundaries/PLPermissionBoundary/PLPermissionBoundary';
import { INVITE_CODE_AGENTS_TAB, INVITE_CODE_INFO_TAB, INVITE_CODE_SALESPERSON_TAB, INVITE_CODE_SHOOTS_TAB } from '../../../../utils/Config/MenuBodyTabs';
import Helmet from 'react-helmet';
import UIMenuBody, { filterSelectedItem } from '../../../../components/UI/UIMenuBody';
import { useApolloClient, useLazyQuery } from '@apollo/react-hooks';
import { GET_INVITE_CODE } from './queries';
import { useRouteMatch } from 'react-router-dom';
import { getIsUserAdminSelector, getSignedInUserSelector } from '../../../../redux/selectors/CurrentUserSelectors';
import { toast } from 'react-toastify';
import InviteCodeAgentsTab from './IndividualInviteCodeTabs/InviteCodeAgentsTab';
import InviteCodeServicesTab from './IndividualInviteCodeTabs/InviteCodeServicesTab';
import InviteCodeSalespersonTab from './IndividualInviteCodeTabs/InviteCodeSalespersonTab';
import InviteCodeInfoTab from './IndividualInviteCodeTabs/InviteCodeInfoTab';

export default function IndividualInviteCode({}) {
	const match = useRouteMatch<MatchRouteType>();
	const signedInUser = useSelector(getSignedInUserSelector);
	let isUserAdmin = useSelector(getIsUserAdminSelector);
	const client = useApolloClient();

	const [inviteCode, setInviteCode] = useState<InviteCodeType | undefined>(undefined);
	const [isLoading, setIsLoading] = useState(false);

	const [getInviteCode, { data: inviteCodeData }] = useLazyQuery(GET_INVITE_CODE, {
		variables: { q: { id: { op_eq: match.params.id } } }
	});

	// Mark: Lifecycle functions

	useEffect(() => {
		(async () => {
			await getInviteCode();
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (inviteCodeData) {
				if (inviteCodeData.forms) {
					let form = inviteCodeData.forms[0];
					if (form.formData.promos) {
						for (let i = 0; i < form.formData.promos.length; i++) {
							let promo: PromoType = form.formData.promos[i];

							if (promo.option) {
								let completion = (index, option) => {
									let promo: PromoType = form.formData.promos[index];
									promo.option = option;
									form.formData.promos[index] = promo;
									setInviteCode(form);
								};
								await queryOption(promo.option, i, completion);
							}
						}
					}
					setIsLoading(false);
					setInviteCode(form);
				}
			}
		})();
	}, [inviteCodeData]);

	if (!inviteCode || !inviteCode.inviteCode) return null;

	//Mark: Component functions

	const getMenuItems = () => {
		let menuItems: MenuItemsType[] = [];

		menuItems.push(INVITE_CODE_INFO_TAB);
		if (isUserAdmin) {
			menuItems.push(INVITE_CODE_SALESPERSON_TAB);
		}
		menuItems.push(INVITE_CODE_SHOOTS_TAB);
		menuItems.push(INVITE_CODE_AGENTS_TAB);

		return menuItems;
	};

	const queryOption = async (option, index, completion) => {
		const response = await client
			.query({
				query: Queries.GET_OPTION,
				variables: { id: option }
			})
			.catch((error) => {
				toast.error(error.message);
			});
		const { option: responseOption } = response['data'];
		if (responseOption) {
			completion(index, responseOption);
		}
	};

	const getPermissionGranted = () => {
		if (!signedInUser) return;
		switch (signedInUser.role) {
			case USER_ROLE_ADMIN:
			case USER_ROLE_REGIONAL_MANAGER:
				return true;
			case USER_ROLE_AFFILIATE:
				return signedInUser.id === inviteCode.salesman?.id;
			default:
				return false;
		}
	};

	const renderSelectedPage = () => {
		const selectedPage = filterSelectedItem(getMenuItems(), location);
		switch (selectedPage) {
			case INVITE_CODE_INFO_TAB:
				return <InviteCodeInfoTab inviteId={match.params.id} />;
			case INVITE_CODE_SALESPERSON_TAB:
				return <InviteCodeSalespersonTab inviteId={match.params.id} />;
			case INVITE_CODE_SHOOTS_TAB:
				return <InviteCodeServicesTab inviteId={match.params.id} />;
			case INVITE_CODE_AGENTS_TAB:
				return <InviteCodeAgentsTab inviteId={match.params.id} />;
			default:
				return null;
		}
	};

	//Mark: Render functions

	if (isLoading) {
		return <UILoadingPage />;
	}

	return (
		<PLPermissionBoundary isGranted={getPermissionGranted()}>
			<UIMenuBody menuItems={getMenuItems()}>
				<Helmet>
					<title>Code {inviteCode.inviteCode ? inviteCode.inviteCode : ''} | Pineapple Labs</title>
					<meta property='og:title' content={`${inviteCode.inviteCode ? inviteCode.inviteCode : ''} | Pineapple Labs`} />
				</Helmet>
				<div className='inviteBody'>{renderSelectedPage()}</div>
			</UIMenuBody>
		</PLPermissionBoundary>
	);
}
